import React, { useState } from 'react';
import Button from '../common/Button';

/**
 * 🔒 GDPRConsentModal — Gate vincolante di consenso informato (Art. 6, 7 e 9 GDPR)
 * 
 * Viene presentato all'utente (Cliente o Counselor) al primo accesso o quando la versione del consenso cambia.
 * Impedisce l'accesso all'applicazione fino all'esplicita accettazione dei consensi obbligatori.
 */
const GDPRConsentModal = ({ user, onAccept, onReject }) => {
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [aiProcessingAccepted, setAiProcessingAccepted] = useState(false);
    const [expandedSection, setExpandedSection] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    const handleAccept = async () => {
        if (!privacyAccepted || !aiProcessingAccepted) return;

        setIsSubmitting(true);
        try {
            const consentData = {
                accepted: true,
                timestamp: new Date().toISOString(),
                version: '1.0',
                clientName: user.name || user.email || 'Utente',
                consents: {
                    gdprGeneral: true,
                    healthAndEmotionalData: true,
                    aiAssistedSupervision: true
                }
            };
            await onAccept(consentData);
        } catch (error) {
            console.error("Errore durante il salvataggio del consenso:", error);
            alert("Si è verificato un errore durante la registrazione del consenso. Riprova.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-fade-in overflow-y-auto">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full my-8 p-6 md:p-8 border border-slate-100 relative">
                
                {/* Header */}
                <div className="flex items-center gap-4 border-b border-slate-100 pb-5 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center text-2xl shadow-sm">
                        🛡️
                    </div>
                    <div>
                        <span className="text-[11px] font-bold tracking-widest text-teal-700 uppercase bg-teal-50 px-2.5 py-0.5 rounded-full border border-teal-100">
                            Regolamento UE 2016/679 (GDPR)
                        </span>
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mt-1">
                            Informativa e Consenso al Trattamento Dati
                        </h2>
                    </div>
                </div>

                {/* Introduzione */}
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                    Benvenuto in <strong>AwarenessTool</strong>. Prima di iniziare, è fondamentale che tu prenda visione di come proteggiamo la tua riservatezza e i tuoi dati personali durante il percorso di counseling.
                </p>

                {/* Box Informative Espandibili */}
                <div className="space-y-3 mb-6">
                    {/* Sezione 1: Finalità e Dati Trattati */}
                    <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/50 hover:bg-slate-50 transition">
                        <button 
                            type="button" 
                            onClick={() => toggleSection('finalita')}
                            className="w-full flex items-center justify-between text-left font-semibold text-slate-800 text-sm"
                        >
                            <span className="flex items-center gap-2">
                                <span>📋</span> Finalità del trattamento e dati particolari (Art. 9)
                            </span>
                            <span className="text-xs text-slate-400 font-bold">{expandedSection === 'finalita' ? '▲ Chiudi' : '▼ Dettagli'}</span>
                        </button>
                        {expandedSection === 'finalita' && (
                            <div className="mt-3 text-xs text-slate-600 space-y-2 border-t border-slate-200/60 pt-3 animate-fade-in leading-relaxed">
                                <p>
                                    I dati raccolti (note di seduta, diari personali, sogni, risposte a questionari e valutazioni esperienziali) sono trattati esclusivamente per le finalità di supporto, monitoraggio e relazione d'aiuto nell'ambito del counseling BioGestalt.
                                </p>
                                <p>
                                    Ai sensi dell'<strong>Art. 9 del GDPR</strong>, il trattamento di dati particolari (stato emotivo, benessere psicofisico) avviene previo tuo esplicito consenso ed è vincolato al segreto professionale deontologico.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Sezione 2: Sicurezza e Conservazione */}
                    <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/50 hover:bg-slate-50 transition">
                        <button 
                            type="button" 
                            onClick={() => toggleSection('sicurezza')}
                            className="w-full flex items-center justify-between text-left font-semibold text-slate-800 text-sm"
                        >
                            <span className="flex items-center gap-2">
                                <span>🔒</span> Sicurezza, Cifratura e Riservatezza
                            </span>
                            <span className="text-xs text-slate-400 font-bold">{expandedSection === 'sicurezza' ? '▲ Chiudi' : '▼ Dettagli'}</span>
                        </button>
                        {expandedSection === 'sicurezza' && (
                            <div className="mt-3 text-xs text-slate-600 space-y-2 border-t border-slate-200/60 pt-3 animate-fade-in leading-relaxed">
                                <p>
                                    I dati sono archiviati su database cloud ad alta sicurezza (Google Firebase Firestore con cifratura in transito e a riposo) protetti da regole di segregazione granulari: solo tu e il tuo counselor assegnato potete accedere ai tuoi contenuti clinici.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Sezione 3: Supporto AI e Anonimizzazione */}
                    <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/50 hover:bg-slate-50 transition">
                        <button 
                            type="button" 
                            onClick={() => toggleSection('ai')}
                            className="w-full flex items-center justify-between text-left font-semibold text-slate-800 text-sm"
                        >
                            <span className="flex items-center gap-2">
                                <span>🤖</span> Elaborazione Assistita e Strumenti AI
                            </span>
                            <span className="text-xs text-slate-400 font-bold">{expandedSection === 'ai' ? '▲ Chiudi' : '▼ Dettagli'}</span>
                        </button>
                        {expandedSection === 'ai' && (
                            <div className="mt-3 text-xs text-slate-600 space-y-2 border-t border-slate-200/60 pt-3 animate-fade-in leading-relaxed">
                                <p>
                                    L'applicazione mette a disposizione del counselor strumenti di intelligenza artificiale per facilitare la redazione di sintesi e la supervisione. Tutti i contenuti inviati all'elaborazione vengono filtrati e protetti per garantire la massima riservatezza.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Sezione 4: Diritti dell'Interessato */}
                    <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/50 hover:bg-slate-50 transition">
                        <button 
                            type="button" 
                            onClick={() => toggleSection('diritti')}
                            className="w-full flex items-center justify-between text-left font-semibold text-slate-800 text-sm"
                        >
                            <span className="flex items-center gap-2">
                                <span>⚖️</span> I tuoi diritti (Oblio, Accesso, Portabilità)
                            </span>
                            <span className="text-xs text-slate-400 font-bold">{expandedSection === 'diritti' ? '▲ Chiudi' : '▼ Dettagli'}</span>
                        </button>
                        {expandedSection === 'diritti' && (
                            <div className="mt-3 text-xs text-slate-600 space-y-2 border-t border-slate-200/60 pt-3 animate-fade-in leading-relaxed">
                                <p>
                                    Hai il pieno diritto in ogni momento di: richiedere l'accesso ai tuoi dati, richiederne la rettifica o la cancellazione definitiva (diritto all'oblio), esportare i tuoi contenuti e revocare il consenso scrivendo a <a href="mailto:info@awarenesstool.it" className="text-teal-600 underline">info@awarenesstool.it</a> o tramite il tuo counselor.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Checkbox di Consenso Obbligatorie */}
                <div className="bg-teal-50/60 border border-teal-200/70 rounded-2xl p-5 mb-6 space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input 
                            type="checkbox" 
                            checked={privacyAccepted}
                            onChange={(e) => setPrivacyAccepted(e.target.checked)}
                            className="mt-1 w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500 cursor-pointer"
                        />
                        <span className="text-xs text-slate-700 font-medium leading-relaxed">
                            <strong className="text-slate-900">* (Obbligatorio)</strong> Ho letto e compreso l'Informativa sulla Privacy e <strong>acconsento esplicitamente</strong> al trattamento dei miei dati personali e particolari relativi al percorso di counseling ai sensi del Regolamento UE 2016/679 (GDPR).
                        </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input 
                            type="checkbox" 
                            checked={aiProcessingAccepted}
                            onChange={(e) => setAiProcessingAccepted(e.target.checked)}
                            className="mt-1 w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500 cursor-pointer"
                        />
                        <span className="text-xs text-slate-700 font-medium leading-relaxed">
                            <strong className="text-slate-900">* (Obbligatorio)</strong> Acconsento all'utilizzo degli strumenti digitali e di elaborazione assistita dell'applicazione per le finalità di sintesi, supervisione e monitoraggio del percorso.
                        </span>
                    </label>
                </div>

                {/* Footer / Azioni */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={onReject}
                        className="text-xs font-semibold text-slate-400 hover:text-red-600 transition order-2 sm:order-1"
                    >
                        Rifiuta ed Esci
                    </button>

                    <Button
                        onClick={handleAccept}
                        disabled={!privacyAccepted || !aiProcessingAccepted || isSubmitting}
                        className={`w-full sm:w-auto px-8 py-3 rounded-2xl text-sm font-bold shadow-lg transition order-1 sm:order-2 ${
                            !privacyAccepted || !aiProcessingAccepted || isSubmitting
                                ? 'opacity-50 cursor-not-allowed bg-slate-300'
                                : 'bg-teal-600 hover:bg-teal-700 text-white shadow-teal-600/20'
                        }`}
                    >
                        {isSubmitting ? 'Registrazione consenso...' : 'Accetta e Continua →'}
                    </Button>
                </div>

            </div>
        </div>
    );
};

export default GDPRConsentModal;
